<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-bold text-neutral-900">常用链接管理</h2>
        <div class="flex gap-3">
          <div class="relative">
            <input
              type="text"
              v-model="searchForm.keyword"
              @input="handleSearch"
              placeholder="搜索链接标题..."
              class="px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm w-64"
            />
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
          </div>
          <!-- <select
            v-model="searchForm.status"
            @change="handleSearch"
            class="px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          >
            <option :value="-1">全部状态</option>
            <option :value="1">正常</option>
            <option :value="0">禁用</option>
          </select> -->
          <button
            @click="refreshList"
            class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
            title="刷新列表"
          >
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </button>
          <button
            @click="openAddDialog"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <i class="fa fa-plus"></i>新增链接
          </button>
        </div>
      </div>

      <!-- 链接列表（两列并排） -->
      <div v-loading="loading" class="space-y-4">
        <div v-if="linkList.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div
            v-for="link in linkList"
            :key="link.id"
            class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div class="flex flex-col sm:flex-row gap-6">
              <!-- 链接信息 -->
              <div class="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div class="flex justify-between items-start mb-2">
                    <h3 class="text-lg font-bold text-neutral-900 mb-1">
                      {{ link.title }}
                    </h3>
                    <span
                      class="text-xs px-2 py-1 rounded-full"
                      :class="{
                        'bg-green-100 text-green-700': link.status === 1,
                        'bg-red-100 text-red-700': link.status === 0,
                      }"
                    >
                      {{ getStatusText(link.status) }}
                    </span>
                  </div>
                  <div class="flex items-center text-sm text-neutral-500 mb-2">
                    <span class="mr-4">
                      <i class="fa fa-link mr-1"></i>
                      <a :href="link.link" target="_blank" class="text-primary hover:underline">{{ link.link }}</a>
                    </span>
                  </div>
                  <div class="flex items-center text-sm text-neutral-500">
                    <span class="mr-4">
                      <i class="fa fa-sort-numeric-asc mr-1"></i>
                      排序: {{ link.orderNum }}
                    </span>
                    <span>
                      <i class="fa fa-clock-o mr-1"></i>
                      {{ formatDate(link.createTime) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 操作按钮 -->
              <div class="flex sm:flex-col justify-end sm:justify-center gap-3 border-t sm:border-t-0 sm:border-l border-neutral-200 pt-4 sm:pt-0 sm:pl-6">
                <!-- <button
                  @click="toggleStatus(link)"
                  class="flex-1 sm:flex-none px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center"
                  :class="link.status === 1 ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'"
                >
                  <i class="fa mr-2" :class="link.status === 1 ? 'fa-ban' : 'fa-check'"></i>
                  {{ link.status === 1 ? '禁用' : '启用' }}
                </button> -->
                <button
                  @click="openEditDialog(link)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-edit mr-2"></i>编辑
                </button>
                <button
                  @click="deleteLink(link)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-neutral-50 text-neutral-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-trash-o mr-2"></i>删除
                </button>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div class="flex justify-center mt-8">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page="page"
              @current-change="handlePageChange"
            />
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="bg-white rounded-xl border border-neutral-200 p-16 text-center"
        >
          <div class="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 text-neutral-400">
            <i class="fa fa-link text-3xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-2">
            暂无常用链接
          </h3>
          <p class="text-neutral-500 text-sm mb-6">
            还没有添加任何常用链接，点击上方按钮添加
          </p>
          <button
            @click="openAddDialog"
            class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            新增链接
          </button>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑链接' : '新增链接'"
      width="500px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">链接标题 <span class="text-red-500">*</span></label>
          <input
            v-model="form.title"
            type="text"
            placeholder="请输入链接标题"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">链接地址 <span class="text-red-500">*</span></label>
          <input
            v-model="form.link"
            type="text"
            placeholder="请输入链接地址，如 https://example.com"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">免登站点标识（选填）</label>
          <input
            v-model="form.ssoTarget"
            type="text"
            placeholder="填写后点击将走免登跳转，留空为普通外链"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">排序号</label>
          <input
            v-model.number="form.orderNum"
            type="number"
            placeholder="数字越小排序越靠前"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <!-- <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">状态</label>
          <select
            v-model="form.status"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          >
            <option :value="1">正常</option>
            <option :value="0">禁用</option>
          </select>
        </div> -->
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            @click="dialogVisible = false"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors text-sm"
          >
            取消
          </button>
          <button
            @click="saveLink"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            确定
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessageBox, ElMessage } from "element-plus";

export default {
  name: "LinkManager",
  data() {
    return {
      linkList: [],
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      searchForm: {
        keyword: "",
        status: -1,
      },
      searchTimer: null,
      // 弹窗
      dialogVisible: false,
      isEdit: false,
      form: {
        id: "",
        title: "",
        link: "",
        ssoTarget: "",
        orderNum: 0,
        status: 1,
      },
    };
  },
  created() {
    this.getLinkList();
  },
  methods: {
    // 获取链接列表
    async getLinkList() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
        };

        if (this.searchForm.keyword) {
          params.searchKey = this.searchForm.keyword;
        }
        if (this.searchForm.status !== -1) {
          params.status = this.searchForm.status;
        }

        const data = await this.$aesEncrypt(params);
        const res = await this.$axios({
          url: "links/list",
          method: "POST",
          data: { data },
        });

        if (res.code === 200) {
          this.linkList = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (error) {
        console.error("获取链接列表失败:", error);
        this.linkList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    // 搜索（防抖）
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.getLinkList();
      }, 300);
    },

    // 刷新列表
    refreshList() {
      this.getLinkList();
    },

    // 分页切换
    handlePageChange(page) {
      this.page = page;
      this.getLinkList();
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        keyword: "",
        status: -1,
      };
      this.page = 1;
      this.getLinkList();
    },

    // 获取状态文本
    getStatusText(status) {
      const map = { 1: "正常", 0: "禁用" };
      return map[status] || "未知";
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const h = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${d} ${h}:${min}`;
    },

    // 打开新增弹窗
    openAddDialog() {
      this.isEdit = false;
      this.form = {
        id: "",
        title: "",
        link: "",
        ssoTarget: "",
        orderNum: 0,
        status: 1,
      };
      this.dialogVisible = true;
    },

    // 打开编辑弹窗
    openEditDialog(link) {
      this.isEdit = true;
      this.form = {
        id: link.id,
        title: link.title,
        link: link.link,
        ssoTarget: link.ssoTarget || "",
        orderNum: link.orderNum || 0,
        status: link.status,
      };
      this.dialogVisible = true;
    },

    // 保存链接（新增/编辑）
    async saveLink() {
      // 表单校验
      if (!this.form.title || !this.form.title.trim()) {
        ElMessage.warning("请输入链接标题");
        return;
      }
      if (!this.form.link || !this.form.link.trim()) {
        ElMessage.warning("请输入链接地址");
        return;
      }

      try {
        const payload = {
          title: this.form.title.trim(),
          link: this.form.link.trim(),
          ssoTarget: this.form.ssoTarget ? this.form.ssoTarget.trim() : "",
          orderNum: this.form.orderNum || 0,
          status: this.form.status,
        };

        if (this.isEdit) {
          payload.id = this.form.id;
        }

        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({
          url: "links",
          method: "PUT",
          data: { data },
        });

        if (res.code === 200) {
          ElMessage.success(this.isEdit ? "编辑成功" : "新增成功");
          this.dialogVisible = false;
          this.getLinkList();
        } else {
          ElMessage.error(res.msg || (this.isEdit ? "编辑失败" : "新增失败"));
        }
      } catch (error) {
        console.error(this.isEdit ? "编辑链接失败:" : "新增链接失败:", error);
        ElMessage.error(this.isEdit ? "编辑失败，请检查网络" : "新增失败，请检查网络");
      }
    },

    // 切换状态
    toggleStatus(link) {
      const newStatus = link.status === 1 ? 0 : 1;
      const actionText = newStatus === 1 ? "启用" : "禁用";
      ElMessageBox.confirm(
        `确定${actionText}链接"${link.title}"吗？`,
        `${actionText}链接`,
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      ).then(async () => {
        try {
          const payload = {
            id: link.id,
            status: newStatus,
          };
          const data = await this.$aesEncrypt(payload);
          const res = await this.$axios({
            url: "links",
            method: "PUT",
            data: { data },
          });
          if (res.code === 200) {
            ElMessage.success(`${actionText}成功`);
            this.getLinkList();
          } else {
            ElMessage.error(res.msg || "操作失败");
          }
        } catch (error) {
          console.error("切换状态失败:", error);
          ElMessage.error("操作失败，请检查网络");
        }
      });
    },

    // 删除链接
    deleteLink(link) {
      ElMessageBox.confirm(
        `确定删除链接"${link.title}"吗？删除后不可恢复`,
        "删除链接",
        {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "error",
        }
      ).then(() => {
        this.$axios({
          url: `links/${link.id}`,
          method: "DELETE",
        }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("删除成功");
            this.getLinkList();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        });
      });
    },
  },
};
</script>
